import React from 'react'
import {
    Routes,
    Route,
    Link,
    Outlet,
    Navigate,
} from 'react-router-dom'

// Third Party
import { withAuthInfo, useLogoutFunction } from '@propelauth/react'
import {
    Box,
    Button,
    ThemeProvider,
    Typography,
} from '@mui/material'

import './App.css'

// Pages
import HomePage from './pages/HomePage'
import Settings from './pages/SettingsPage'
import DocumentPage from './pages/DocumentPage'
import QueryDetails from './pages/SettingsPage/sections/DatabaseSections/modals/QueryDetailsModal'
import TemplatesPage from './pages/TemplatesPages/TemplatesPage'
import DocumentSetsPage from './pages/DocumentSetsPage'
import EditTemplatePage from './pages/TemplatesPages/EditTemplatePage'
import CreateTemplatePage from './pages/TemplatesPages/CreateTemplatePage'
import CreateDocumentPage from './pages/CreateDocumentPage'
import SourceDocumentsPage from './pages/SourceDocumentsPage'
import ChatPage from './pages/ChatPage.tsx'
import UsersAndPermissionsPage from './pages/SettingsPage/sections/UsersAndPermissionsSections/UsersAndPermissionsPage'

// Components
import Sidebar from './components/nav/sidebar'
import User from './components/User'

// Hooks

// Icons
import logo from './icons/LOGO.svg'

// Contexts
import { SidebarProvider } from './contexts/SidebarContext'
import { DemoProvider } from './contexts/DemoContext'
import { ArtosTheme } from './components/Themes'
import { WebViewerProvider } from './contexts/WebViewerContext'
import { UserProvider } from './contexts/UserContext'
import { DatabaseProvider } from './contexts/DatabaseContext'
import { QueryProvider } from './contexts/QueryContext'
import { DocumentTypeProvider } from './contexts/DocumentTypeContext'
import { SectionTabProvider } from './contexts/SectionTabContext'
import { StyleGuideProvider } from './contexts/StyleGuideContext'
import { InstructionalMRTProvider } from './contexts/InstructionalMRTContext'
import { ComponentLibraryDemoProvider } from './contexts/ComponentLibraryDemoContext'
import { DocumentCreationProvider } from './contexts/DocumentCreationContext'

// Define your page components here
const About = () => (
    <div>
        <h2>About</h2>
    </div>
)
const Dashboard = () => (
    <div>
        <h2>Dashboard</h2>
    </div>
)
const NoMatch = () => (
    <div>
        <div>No page found!</div>
        <h2>Nothing to see here.</h2>
        <p>
            <Link to="/">Go to the home page</Link>
        </p>
    </div>
)

const Help = () => (
    <div
        className="full-width-container"
        style={{ backgroundColor: '#F9FAFB' }}
    >
        <h2>Contact info@artosai.com for help and settings</h2>
    </div>
)

function Layout() {
    return (
        <Box
            sx={{
                display: 'flex',
                flexGrow: 1,
                height: '100%',
                overflow: 'hidden'
            }}
        >
            <Sidebar />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    overflow: 'auto',
                    height: '100%',
                    padding: '0px',
                }}
            >
                <Outlet />
            </Box>
        </Box>
    )
}

function App(props) {
    const logout = useLogoutFunction()
    const orgs = props.orgHelper.getOrgs()
    const org = orgs[0].orgId

    return props.isLoggedIn ? (
        <UserProvider>
            <ComponentLibraryDemoProvider>
                <DocumentTypeProvider>
                    <DatabaseProvider>
                        <QueryProvider>
                            {/* <StyleGuideProvider> */}
                                <DemoProvider>
                                    <SidebarProvider>
                                        <WebViewerProvider>
                                            <InstructionalMRTProvider>
                                                <ThemeProvider theme={ArtosTheme}>
                                                    <SectionTabProvider>
                                                        <DocumentCreationProvider>
                                                        <User />
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                height: '100vh',
                                                                overflow: 'hidden',
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems:
                                                                        'center',
                                                                    justifyContent:
                                                                        'space-between',
                                                                    p: 2,
                                                                    borderBottom:
                                                                        '1px solid #e0e0e0',
                                                                    height: '64px',
                                                                    backgroundColor:
                                                                        '#ffffff',
                                                                    zIndex: 1100,
                                                                    position:
                                                                        'relative',
                                                                }}
                                                            >
                                                                <ThemeProvider
                                                                    theme={ArtosTheme}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            display:
                                                                                'flex',
                                                                            alignItems:
                                                                                'center',
                                                                        }}
                                                                    >
                                                                        <img
                                                                            src={logo}
                                                                            style={{
                                                                                marginRight:
                                                                                    '8px',
                                                                            }}
                                                                            alt="Logo"
                                                                        />
                                                                        <Typography
                                                                            variant="h6"
                                                                            component="div"
                                                                        >
                                                                            Artos
                                                                        </Typography>
                                                                    </Box>
                                                                    <Button
                                                                        onClick={logout}
                                                                        sx={{
                                                                            color: '#1f477d',
                                                                        }}
                                                                    >
                                                                        Log Out
                                                                    </Button>
                                                                </ThemeProvider>
                                                            </Box>
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    flexGrow: 1,
                                                                    overflow: 'hidden',
                                                                }}
                                                            >
                                                                <Routes>
                                                                    <Route
                                                                        path="/"
                                                                        element={
                                                                            <Layout />
                                                                        }
                                                                    >
                                                                        <Route
                                                                            index
                                                                            element={
                                                                                <HomePage />
                                                                            }
                                                                        />
                                                                        <Route
                                                                            path="templates"
                                                                            element={
                                                                                <TemplatesPage />
                                                                            }
                                                                        />
                                                                        <Route
                                                                            path="source-files"
                                                                            element={
                                                                                <SourceDocumentsPage />
                                                                            }
                                                                        />
                                                                        <Route
                                                                            path="documentSet/:id"
                                                                            element={
                                                                                <DocumentSetsPage
                                                                                    authProps={
                                                                                        props
                                                                                    }
                                                                                />
                                                                            }
                                                                        />
                                                                        <Route
                                                                            path="document/:id"
                                                                            element={
                                                                                <DocumentPage />
                                                                            }
                                                                        />
                                                                        <Route
                                                                            path="createDocument/:documentSetId"
                                                                            element={
                                                                                <CreateDocumentPage />
                                                                            }
                                                                        />
                                                                        <Route
                                                                            path="editTemplate/:id"
                                                                            element={
                                                                                <EditTemplatePage />
                                                                            }
                                                                        />
                                                                        <Route
                                                                            path="createTemplate"
                                                                            element={
                                                                                <CreateTemplatePage />
                                                                            }
                                                                        />
                                                                        <Route
                                                                            path="chat"
                                                                            element={<ChatPage/>
                                                                            }
                                                                        />
                                                                        <Route
                                                                            path="*"
                                                                            element={
                                                                                <NoMatch />
                                                                            }
                                                                        />
                                                                        {/* <Route
                                                                            path="individualAutomationPage"
                                                                            element={
                                                                                <IndividualAutomationPage />
                                                                            }
                                                                        />
                                                                        <Route
                                                                            path="about"
                                                                            element={
                                                                                <About />
                                                                            }
                                                                        />
                                                                        <Route
                                                                            path="dashboard"
                                                                            element={
                                                                                <Dashboard />
                                                                            }
                                                                        />
                                                                        <Route
                                                                            path="automations"
                                                                            element={
                                                                                <AutomationsPage />
                                                                            }
                                                                        />
                                                                        <Route
                                                                            path="settings/help"
                                                                            element={
                                                                                <Help />
                                                                            }
                                                                        />
                                                                        */}
                                                                        <Route
                                                                            path="settings"
                                                                            element={
                                                                                <Settings
                                                                                    props={
                                                                                        props
                                                                                    }
                                                                                />
                                                                            }
                                                                        >
                                                                            <Route
                                                                                index
                                                                                element={
                                                                                    <Navigate to="/settings/users-permissions" replace />
                                                                                }
                                                                            />
                                                                            <Route
                                                                                path="users-permissions"
                                                                                element={
                                                                                    <UsersAndPermissionsPage />
                                                                                }
                                                                            />
                                                                            {/* <Route
                                                                                path="database-connections"
                                                                                element={
                                                                                    <DatabaseConnections />
                                                                                }
                                                                            />
                                                                            <Route
                                                                                path="query-builder"
                                                                                element={
                                                                                    <QueryBuilderSection />
                                                                                }
                                                                            />
                                                                            <Route
                                                                                path="query-builder/:queryId"
                                                                                element={
                                                                                    <QueryDetails />
                                                                                }
                                                                            /> */}
                                                                        </Route>
                                                                    </Route>
                                                                </Routes>
                                                            </Box>
                                                        </Box>
                                                        
                                                    </DocumentCreationProvider>
                                                </SectionTabProvider>
                                            </ThemeProvider>
                                            </InstructionalMRTProvider>
                                        </WebViewerProvider>
                                    </SidebarProvider>
                                </DemoProvider>
                            {/* </StyleGuideProvider> */}
                        </QueryProvider>
                    </DatabaseProvider>
                </DocumentTypeProvider>
            </ComponentLibraryDemoProvider>
        </UserProvider>
    ) : (
        <div>Please sign in</div>
    )
}


export default withAuthInfo(App)
